import { useEffect, useState } from "react";
import {
  CheckCircle2, CalendarDays, Wallet, School, Clock, ArrowRight, Award, ShieldCheck, MapPin,
} from "lucide-react";
import { COURSE_LIST } from "@/data/courses";
import BookingDialog from "@/components/site/BookingDialog";
import LpShell, { useLpMeta, GoogleStarsBadge } from "@/pages/lp/LpShell";
import LpLeadForm from "@/pages/lp/LpLeadForm";
import TrackedPhoneLink from "@/components/site/TrackedPhoneLink";
import { injectJsonLd, removeJsonLd } from "@/lib/seo";

/**
 * Landing-Page „Externenprüfung Pflegefachassistenz" — mit Partnerschule
 * in NRW, garantierter Prüfungsplatz, Preis getrennt ausgewiesen (1.990 € + 500 €).
 * Final-URL: /externenpruefung-pflegefachassistenz
 */
const USPS = [
  { icon: School, text: "Prüfungsplatz über unsere Partnerschulen in NRW" },
  { icon: Award, text: "Anerkannter Vorbereitungskurs · 300 UE · 14 Lernfelder" },
  { icon: ShieldCheck, text: "Kleine Prüfungsgruppen – genug Plätze, kein Wartelisten-Frust" },
  { icon: Wallet, text: "1.990 € Kursgebühr + 500 € Prüfungsgebühr = 2.490 € Gesamt – transparent" },
];

const FAQ = [
  ["Wie läuft die externe Prüfung zur Pflegefachassistenz ab?",
   "Du absolvierst bei uns den Vorbereitungskurs (300 UE, 14 Lernfelder) und meldest dich direkt bei einer unserer Partnerschulen in NRW zur staatlichen Prüfung an. Die Prüfung selbst umfasst einen schriftlichen, mündlichen und praktischen Teil und wird von der Prüfungsschule organisiert. Wir stellen den Kontakt her und begleiten dich bis zum Prüfungstag."],
  ["Wie setzt sich der Gesamtpreis von 2.490 € zusammen?",
   "Der Gesamtpreis setzt sich zusammen aus 1.990 € Kursgebühr für den kompletten 300-UE-Vorbereitungskurs inklusive aller Kursunterlagen, Übungen und persönlicher Prüfungsbegleitung – sowie 500 € Prüfungsgebühr für die staatliche Externenprüfung. Die Prüfungsgebühr wird von der Prüfstelle erhoben und 1:1 an dich weitergegeben, sie ist im Gesamtpreis bereits enthalten. Keine versteckten Kosten."],
  ["Bekomme ich wirklich einen Prüfungsplatz?",
   "Ja. Über unsere langjährigen Partnerschaften mit anerkannten Prüfungsschulen in NRW sind ausreichend Prüfungsplätze für unsere Absolvent:innen reserviert. Du landest nicht auf einer Warteliste – dein Prüfungstermin steht mit deiner Kursanmeldung fest."],
  ["Welche Voraussetzungen brauche ich für die Externenprüfung?",
   "Für die staatliche Externenprüfung zur Pflegefachassistenz brauchst du in der Regel den Nachweis einer mindestens dreijährigen einschlägigen Berufstätigkeit in der Pflege sowie einen Hauptschulabschluss (oder gleichwertig). Die genauen Voraussetzungen prüfen wir kostenlos mit dir vor der Anmeldung."],
  ["Kann ich den Kurs online machen und wann kann ich starten?",
   "Ja – der komplette Vorbereitungskurs läuft online-live per Zoom mit unseren Dozent:innen. Kursstart ist jeden Montag – du kannst also flexibel einsteigen. Wie schnell du die 300 UE durcharbeitest, entscheidest du selbst: Manche brauchen dafür wenige Wochen, andere lassen es entspannter angehen. Die praktische Prüfungsphase findet danach vor Ort in einer unserer Partnerschulen in NRW statt."],
  ["Was, wenn ich durch die Prüfung falle?",
   "Kein Weltuntergang. Nach § 20 PflAPrV ist ein Wiederholungsversuch pro Prüfungsteil möglich. Wir bereiten dich gezielt vor und helfen bei der Nachprüfungs-Anmeldung. Unsere Prüfungsquote spricht für sich – aber falls es dich trifft, lassen wir dich nicht hängen."],
];

export default function LpExternenpruefungPfa() {
  useLpMeta(
    "Externenprüfung Pflegefachassistenz · Prüfungsplatz über Partnerschulen (MG) | Dickies-Campus",
    "Vorbereitungskurs (300 UE) auf die externe Pflegefachassistenz-Prüfung. Prüfungsplatz über Partnerschulen in NRW. 1.990 € Kursgebühr zzgl. 500 € Prüfungsgebühr – Gesamtpreis 2.490 €."
  );
  const [bookingCourse, setBookingCourse] = useState(null);
  const course = COURSE_LIST.find((c) => c.id === "pflegefachassistent-externenpruefung");

  useEffect(() => {
    injectJsonLd("ld-faq-externpfa", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQ.map(([q, a]) => ({
        "@type": "Question", "name": q,
        "acceptedAnswer": { "@type": "Answer", "text": a },
      })),
    });
    return () => removeJsonLd("ld-faq-externpfa");
  }, []);

  return (
    <LpShell>
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-12 lg:pt-20" data-testid="lp-pfa-hero">
        <div className="max-w-3xl">
          <GoogleStarsBadge />
          <span className="mt-6 inline-flex items-center gap-2 rounded-full bg-terra/10 text-terra px-4 h-8 text-[12px] font-bold uppercase tracking-[0.18em]">
            <MapPin className="h-3.5 w-3.5" strokeWidth={2} /> Partnerschulen in NRW
          </span>
          <h1 data-testid="lp-pfa-headline" className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight text-sand-800 leading-[1.05]">
            Externenprüfung<br />
            <span className="text-terra">Pflegefachassistenz.</span>
          </h1>
          <p className="mt-5 text-sand-600 text-base md:text-lg leading-relaxed max-w-xl">
            Der komplette Vorbereitungskurs – und danach die staatliche Prüfung
            über unsere <strong className="text-sand-800">Partnerschulen in NRW</strong>.
            Prüfungsplatz über Partnerschulen, keine Wartelisten, transparente Preise.
          </p>
          <ul className="mt-7 grid sm:grid-cols-2 gap-3 max-w-xl">
            {USPS.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-start gap-2.5 text-[14px] text-sand-700">
                <Icon className="h-5 w-5 text-terra shrink-0 mt-0.5" strokeWidth={1.7} />
                <span>{text}</span>
              </li>
            ))}
          </ul>
          <div className="mt-9 flex flex-wrap gap-4">
            <a href="#lp-kurs" data-testid="lp-pfa-cta-primary"
              className="inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra-dark text-white px-7 py-3.5 text-base font-semibold transition-colors group">
              Kurs & Preise ansehen
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.8} />
            </a>
            <a href="#lp-kontakt" data-testid="lp-pfa-cta-beratung"
              className="inline-flex items-center rounded-full border border-sand-400 hover:border-sand-800 text-sand-800 px-7 py-3.5 text-base font-medium transition-colors">
              Kostenlose Beratung
            </a>
          </div>
        </div>
      </section>

      {/* Info-Callout Preis + Prüfungsgebühr */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="rounded-3xl border-2 border-terra/30 bg-peach-50 p-6 md:p-8" data-testid="lp-pfa-price-callout">
          <div className="flex items-start gap-5">
            <div className="hidden sm:flex h-12 w-12 rounded-full bg-terra text-white items-center justify-center shrink-0">
              <Wallet className="h-6 w-6" strokeWidth={1.8} />
            </div>
            <div>
              <h2 className="font-display text-2xl text-sand-800">
                Gesamtpreis 2.490 € <span className="text-terra">·</span>{" "}
                <span className="text-[14px] font-medium text-sand-700 tracking-normal">
                  1.990 € Kursgebühr + 500 € Prüfungsgebühr
                </span>
              </h2>
              <p className="mt-3 text-[14.5px] text-sand-700 leading-relaxed max-w-2xl">
                Die <strong>Kursgebühr von 1.990 €</strong> deckt den kompletten{" "}
                <strong>Vorbereitungskurs (300 UE)</strong>, alle Unterlagen und deine
                Prüfungsbegleitung ab. Die <strong>Prüfungsgebühr von 500 €</strong> wird
                von der Prüfstelle (Partner-Prüfungsschule in NRW) erhoben und 1:1 an dich
                weitergegeben – sie ist im Gesamtpreis bereits enthalten.
                Keine versteckten Kosten.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Was ist im Kurs enthalten */}
      <section id="lp-kurs" className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-display text-3xl text-sand-800 mb-3">Was du im Vorbereitungskurs bekommst</h2>
        <p className="text-sand-600 text-[14.5px] mb-8 max-w-2xl">
          300 UE Fachunterricht in 14 Lernfeldern – online-live mit erfahrenen
          Dozent:innen. Danach direkt in die staatliche Prüfung.
        </p>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Award, title: "Kompletter 300-UE-Kurs", body: "Alle 14 Lernfelder nach landesrechtlichen Vorgaben – schriftlich, mündlich, praktisch vorbereitet." },
            { icon: School, title: "Prüfungsplatz gesichert", body: "Kein Warten, kein Bittstellen: Prüfungsplatz über unsere MG-Partnerschule ist mit deiner Anmeldung reserviert." },
            { icon: ShieldCheck, title: "Prüfungsbegleitung", body: "Wir begleiten dich bis zum Prüfungstag – Anmeldung, Vorbereitung, Nachprüfungs-Support falls nötig." },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="bg-white border border-cream-300 rounded-3xl p-7 flex flex-col shadow-soft">
              <Icon className="h-8 w-8 text-terra" strokeWidth={1.5} />
              <h3 className="mt-4 font-display text-xl text-sand-800">{title}</h3>
              <p className="mt-2 text-[14px] text-sand-700 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        {/* Kurskarte */}
        {course && (
          <div className="mt-10 bg-white border border-cream-300 rounded-3xl p-7 md:p-9 shadow-soft" data-testid="lp-pfa-course-card">
            <div className="flex flex-wrap items-start gap-6 justify-between">
              <div className="max-w-xl">
                <span className="rounded-full bg-sand-800 text-cream-50 px-4 h-7 inline-flex items-center text-[11px] font-bold uppercase tracking-[0.16em]">
                  Kursstart · {course.nextStart}
                </span>
                <h3 className="mt-4 font-display text-2xl text-sand-800">{course.title}</h3>
                <p className="text-[14px] text-sand-600 mt-1.5">{course.subtitle}</p>
                <div className="mt-4 space-y-1.5 text-[13.5px] text-sand-700">
                  <div className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-terra" strokeWidth={1.7} /> {course.weeks} · {course.duration}</div>
                  <div className="flex items-center gap-2"><CalendarDays className="h-3.5 w-3.5 text-terra" strokeWidth={1.7} /> Start: {course.nextStart}</div>
                  <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-terra" strokeWidth={1.7} /> Prüfung: Partnerschulen in NRW</div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className="font-display text-3xl font-semibold text-sand-800">2.490 €</span>
                <span className="text-[12px] text-sand-600 font-medium">1.990 € Kursgebühr + 500 € Prüfungsgebühr der Prüfstelle</span>
                <button type="button" data-testid="lp-pfa-book"
                  onClick={() => setBookingCourse({ ...course, price: "€ 2.490" })}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-terra hover:bg-terra-dark text-white px-6 py-3 text-[14px] font-semibold transition-colors">
                  Jetzt Platz sichern <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                </button>
              </div>
            </div>
            {course.curriculum && (
              <div className="mt-6 pt-6 border-t border-cream-200 grid sm:grid-cols-2 gap-2">
                {course.curriculum.slice(0, 8).map((t) => (
                  <div key={t} className="flex items-start gap-2 text-[13.5px] text-sand-700">
                    <CheckCircle2 className="h-4 w-4 text-terra shrink-0 mt-0.5" strokeWidth={1.8} />
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* FAQ */}
      <section className="bg-cream-100 border-y border-cream-300 py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="font-display text-3xl text-sand-800 mb-8">Häufige Fragen zur Externenprüfung</h2>
          <div className="space-y-4">
            {FAQ.map(([q, a]) => (
              <div key={q} className="bg-white rounded-3xl border border-cream-300 p-6" data-testid="lp-pfa-faq-item">
                <h3 className="font-semibold text-sand-800 text-[15.5px]">{q}</h3>
                <p className="mt-2 text-[14px] text-sand-600 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Kontakt */}
      <section id="lp-kontakt" className="bg-brand-rose py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl text-brand-darkred leading-tight">
              Bereit für die staatliche Prüfung?<br />
              <span className="text-white">Sichere dir jetzt deinen Platz.</span>
            </h2>
            <p className="mt-4 text-brand-darkred/80 text-[15px] leading-relaxed max-w-md">
              Wir prüfen kostenlos, ob deine Voraussetzungen für die
              Externenprüfung erfüllt sind – und reservieren dir bei Zusage
              direkt einen Platz an unserer Partnerschule.
            </p>
            <TrackedPhoneLink source="lp-externpfa" className="mt-6 inline-flex items-center gap-2 text-lg font-semibold text-brand-darkred hover:text-white transition-colors">
              📞 0211 29273330
            </TrackedPhoneLink>
          </div>
          <LpLeadForm source="ads-lp-externpfa" audience="pflegekraft"
            sub="Sag uns kurz, wo du in der Pflege stehst – wir prüfen deine Voraussetzungen für die Externenprüfung und reservieren einen Prüfungsplatz." />
        </div>
      </section>

      <BookingDialog course={bookingCourse} open={!!bookingCourse}
        onOpenChange={(v) => !v && setBookingCourse(null)}
        initialBuyerType="selbstzahler" />
    </LpShell>
  );
}
